<script setup lang="ts">
import { gsap } from 'gsap'

const props = defineProps<{
  slug: string
}>()

const { tm, rt } = useI18n()
const section = ref<HTMLElement | null>(null)

const items = computed(() => (tm(`page.business_types.${props.slug}.faq.items`) as { q: unknown, a: unknown }[]).map(item => ({
  question: rt(item.q),
  answer: rt(item.a),
})))

const openFaqIndex = ref<number | null>(0)

const toggleFaq = (index: number) => {
  openFaqIndex.value = openFaqIndex.value === index ? null : index
}

// Smooth height + fade transitions for the accordion panels. CSS can't animate
// to `height: auto`, so GSAP measures scrollHeight at enter time and tweens
// height/opacity, then releases the explicit height after the tween so the
// panel re-flows naturally if content changes.
function onEnter(el: Element, done: () => void) {
  const target = el as HTMLElement
  const height = target.scrollHeight
  target.style.overflow = 'hidden'
  gsap.fromTo(
    target,
    { height: 0, opacity: 0 },
    {
      height,
      opacity: 1,
      duration: 0.35,
      ease: 'power2.out',
      onComplete: () => {
        target.style.height = ''
        target.style.overflow = ''
        done()
      },
    },
  )
}

function onLeave(el: Element, done: () => void) {
  const target = el as HTMLElement
  target.style.overflow = 'hidden'
  gsap.fromTo(
    target,
    { height: target.scrollHeight, opacity: 1 },
    {
      height: 0,
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in',
      onComplete: done,
    },
  )
}

onMounted(() => {
  const el = section.value
  if (!el) return

  gsap.fromTo(el.querySelector('.faq-heading'),
    { opacity: 0, y: 30 },
    {
      opacity: 1, y: 0, duration: 0.8, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 82%', once: true },
    },
  )

  gsap.fromTo(el.querySelectorAll('.faq-item'),
    { opacity: 0, y: 24 },
    {
      opacity: 1, y: 0, duration: 0.55, ease: 'power2.out', stagger: 0.08,
      scrollTrigger: { trigger: el.querySelector('.faq-list'), start: 'top 86%', once: true },
    },
  )
})
</script>

<template>
  <section ref="section" class="py-12 md:py-16 lg:py-20 bg-white relative overflow-hidden">
    <div class="absolute inset-0 bg-[radial-gradient(circle,_#d1d5db_1.5px,_transparent_1.5px)] [background-size:28px_28px] opacity-50 pointer-events-none" aria-hidden="true" />
    <div class="container relative max-w-3xl">
      <div class="faq-heading text-center mb-10 md:mb-12">
        <h2 class="text-[clamp(1.75rem,3.5vw,2.75rem)] font-bold leading-[1.25] text-deep-indigo">
          {{ $t(`page.business_types.${props.slug}.faq.title`) }}
        </h2>
      </div>

      <div class="faq-list flex flex-col gap-5">
        <div
          v-for="(faq, index) in items"
          :key="index"
          class="faq-item bg-white border-2 border-gray-900 rounded shadow-[6px_6px_0_#111827] overflow-hidden">
          <button
            class="flex items-center justify-between gap-4 md:gap-6 px-5 md:px-7 py-5.5 text-start text-base md:text-[1.35rem] font-bold text-gray-900 cursor-pointer bg-transparent border-none w-full transition-colors"
            :aria-expanded="openFaqIndex === index"
            @click="toggleFaq(index)">
            <span>{{ faq.question }}</span>
            <!-- +/− toggle: container does a half-spin while the second bar
                 rotates 90° in sync, so "+" smoothly resolves to "−" without
                 any popping or scaling. -->
            <span
              class="size-9 min-w-9 shrink-0 flex items-center justify-center relative transition-transform duration-[450ms] ease-[cubic-bezier(0.65,0,0.35,1)]"
              :class="openFaqIndex === index ? 'rotate-180' : 'rotate-0'">
              <span class="absolute w-[18px] h-[2.5px] bg-gray-900 rounded-full" />
              <span
                class="absolute w-[18px] h-[2.5px] bg-gray-900 rounded-full origin-center transition-transform duration-[450ms] ease-[cubic-bezier(0.65,0,0.35,1)]"
                :class="openFaqIndex === index ? 'rotate-0' : 'rotate-90'" />
            </span>
          </button>
          <Transition :css="false" @enter="onEnter" @leave="onLeave">
            <div v-if="openFaqIndex === index" class="px-5 md:px-7 pb-5.5 text-sm md:text-base font-medium text-gray-900 leading-[1.75]">
              <p>{{ faq.answer }}</p>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </section>
</template>
